import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';

import cashierService from '@services/cashier';

import { openGenericModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import DeepsightFiltersButton from '@orders/components/FilterButton';

import { getGroupsByStoreIds } from '@productions/components/AnalyticsContainer/services';

import { Button } from '@commons/utils/styledLibraryComponents';
import { DATAGRID_LOCALES, generateDates } from '@commons/utils/dataGrid';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import {
  GenericContentContainer,
  GenericGapContainer,
  GenericPageContainer,
} from '@commons/Layout/styledComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getTheme } from '@commons/utils/theme';
import { getUserTimezone } from '@commons/utils/date';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';

import { getClientInfo } from '@selectors/client';

import { COMMON_DATAGRID_PROPS } from './datagridProps';
import { exportFtpDebugList } from './exportList';
import {
  FilterActiveIcon,
  FilterButtonWrapper,
  LegendButtonContainer,
  LegendButtonIcon,
  StatusDot,
  StatusDropdownItem,
} from './styledComponents';
import { getColumns } from './getColumns';
import { getStatusColor, STATUS_GROUPS } from './constants';
import { getStatusLegendModalParams } from './modals';

const theme = getTheme();

const FtpDebug = (props) => {
  const {
    match,
    user,
    stores,
    currency,
    showErrorMessage,
    openGenericModal,
    client: { storeName },
  } = props;

  const USER_TIMEZONE = getUserTimezone();
  const YESTERDAY = moment.tz(USER_TIMEZONE).subtract(1, 'day');

  // Several raw API statuses share the same color/meaning
  const statusDropdownItems = STATUS_GROUPS.map(({ id, statuses, labelKey }) => {
    const name = i18next.t(labelKey);

    return {
      id,
      name,
      itemValue: statuses,
      renderValue: () => (
        <StatusDropdownItem font={theme.dropdown.dropdownItemText.default.font}>
          <StatusDot color={getStatusColor(statuses[0])} />
          {name}
        </StatusDropdownItem>
      ),
    };
  });

  /*******************
   ***** STATES *****
   *******************/

  const [isLoading, setIsLoading] = useState(false);

  const [selectedStores, setSelectedStores] = useState(stores);

  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Statut filter: keeps only the stores having at least one selected status in the period
  const [selectedStatuses, setSelectedStatuses] = useState(statusDropdownItems);

  const [filters, setFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(true);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [columnsFilterList] = useState([]);

  const [rows, setRows] = useState([]);

  const periodPickerState = usePeriodDatePickerState(
    moment(YESTERDAY).subtract(1, 'month'),
    YESTERDAY,
    USER_TIMEZONE,
  );

  const translatedStoreName = getClientStoreNameTranslation(storeName, false);

  // Keep an array corresponding to the selected period
  const selectedDates = useMemo(() => {
    if (!periodPickerState.startDate || !periodPickerState.endDate) {
      return [];
    }

    return generateDates(periodPickerState.startDate, periodPickerState.endDate);
  }, [periodPickerState.startDate, periodPickerState.endDate]);

  // Max selectable period is 3 months
  const maxFutureDate = useMemo(() => {
    const maxEndDate = moment.tz(periodPickerState.startDate, USER_TIMEZONE).add(3, 'month');

    return moment.min(YESTERDAY, maxEndDate);
  }, [periodPickerState.startDate]);

  // Flattens the selected color groups back into raw API statuses
  const selectedStatusValues = useMemo(
    () => selectedStatuses.flatMap(({ itemValue }) => itemValue),
    [selectedStatuses],
  );

  const isAnyFilterActive = useMemo(
    () =>
      selectedStatuses.length !== STATUS_GROUPS.length ||
      selectedStores.length !== stores.length ||
      selectedGroups.length !== groups.length,
    [
      selectedStatuses.length,
      selectedStores.length,
      stores.length,
      selectedGroups.length,
      groups.length,
    ],
  );

  // Keeps only the rows having, within the selected period, at least one day matching a selected
  // status — the API's status is final (closing days and partial by-POS reporting included)
  const filteredRows = useMemo(() => {
    if (selectedStatuses.length === STATUS_GROUPS.length) {
      return rows;
    }

    return rows.filter(({ store, ...cellsByDate }) =>
      selectedDates.some((date) => {
        const cell = cellsByDate[date];

        return !!cell && selectedStatusValues.includes(cell.status);
      }),
    );
  }, [rows, selectedDates, selectedStatuses.length, selectedStatusValues]);

  // Datagrid columns
  const columns = useMemo(() => {
    if (!selectedDates.length) {
      return [];
    }

    return getColumns(selectedDates, currency, translatedStoreName, filteredRows.length);
  }, [selectedDates, filteredRows.length]);

  const apiRef = useGridApiRef();
  const autoScrollDoneRef = useRef(false); // Tracks whether the scroll has happened
  const hasSelectedAllStoresByDefaultRef = useRef(false);

  /*******************
   ***** EFFECTS *****
   *******************/

  useEffect(() => {
    if (!stores.length || hasSelectedAllStoresByDefaultRef.current) {
      return;
    }

    setSelectedStores(stores);
    hasSelectedAllStoresByDefaultRef.current = true;
  }, [stores]);

  useEffect(() => {
    if (!stores.length) {
      return;
    }

    (async () => {
      const storeIds = stores.map(({ id }) => id);

      const formattedGroups = await getGroupsByStoreIds(storeIds, showErrorMessage);

      setGroups(formattedGroups);
      setSelectedGroups(formattedGroups);
    })();
  }, [stores]);

  const handleExportList = () => {
    try {
      exportFtpDebugList({
        filteredRows,
        selectedDates,
        currency,
        startDate: periodPickerState.startDate,
        endDate: periodPickerState.endDate,
        timezone: USER_TIMEZONE,
      });
    } catch {
      showErrorMessage(i18next.t('GENERAL.EXPORT_FAILURE'));
    }
  };

  // Fetch FTP Statuses on stores and period change
  useEffect(() => {
    if (!selectedStores.length || !periodPickerState.startDate || !periodPickerState.endDate) {
      return;
    }

    (async () => {
      setIsLoading(true);

      try {
        const storeIds = selectedStores.map(({ id }) => id);
        const startDate = moment(periodPickerState.startDate).format(
          DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
        );
        const endDate = moment(periodPickerState.endDate).format(
          DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
        );

        const result = await cashierService.getFtpStatusByStoreIdsAndDate(
          storeIds,
          startDate,
          endDate,
        );

        // Format data for Datagrid: [{ id: storeId, storeName: storeName, date1: { status: status, turnover: turnover, turnoverByPos }, ... }]
        const formattedResult = Object.entries(result).flatMap(([storeId, ftpStatusByStore]) => {
          const associatedStore = selectedStores.find(({ id }) => id === storeId);

          if (!associatedStore) {
            return [];
          }

          const distinctPosNames = new Set(
            ftpStatusByStore.flatMap(({ turnoverByPos }) =>
              (turnoverByPos ?? []).map(({ posName }) => posName),
            ),
          );
          const cashierStoresMapping = [...distinctPosNames].map((posName) => ({ posName }));

          return [
            ftpStatusByStore.reduce(
              (acc, { date, status, turnover, turnoverByPos }) => {
                acc[date] = { status, turnover, turnoverByPos: turnoverByPos ?? [] };
                return acc;
              },
              {
                id: storeId,
                storeName: associatedStore.name,
                store: { ...associatedStore, cashierStoresMapping },
              },
            ),
          ];
        });

        setRows(formattedResult);
      } catch {
        showErrorMessage(i18next.t('ADMIN.FTP_DEBUG.FETCH_FTP_STATUS_ERROR'));

        setRows([]);
      } finally {
        setIsLoading(false);
      }
    })();

    autoScrollDoneRef.current = false;
  }, [selectedStores, periodPickerState.startDate, periodPickerState.endDate]);

  // Auto-scroll to the far right when the grid finished loading
  useEffect(() => {
    const handleRenderedRowsIntervalChange = () => {
      if (apiRef.current && columns.length && !autoScrollDoneRef.current) {
        // Scroll to the far right
        apiRef.current.scrollToIndexes({
          colIndex: columns.length - 1,
        });
        autoScrollDoneRef.current = true;
      }
    };

    // Ensure the event is set up when the grid has rendered rows
    // subscribeEvent() returns the cleanup callback
    return apiRef.current.subscribeEvent(
      'renderedRowsIntervalChange',
      handleRenderedRowsIntervalChange,
    );
  }, [apiRef, columns]);

  return (
    <GenericPageContainer>
      <NavigationBreadCrumb featurePath={match.path} />
      <GenericContentContainer>
        <LeftRightSplitter
          left={
            <GenericGapContainer>
              <FilterButtonWrapper>
                <DeepsightFiltersButton
                  advancedFilters={advancedFilters}
                  applyFilters={applyFilters}
                  columnsFilterList={columnsFilterList}
                  customMultipleDropDowns={[
                    {
                      id: 'status',
                      icon: '/images/inpulse/eye-black-small.svg',
                      list: statusDropdownItems,
                      defaultSelectedItems: statusDropdownItems,
                      selectedItems: selectedStatuses,
                      setSelectedItems: setSelectedStatuses,
                      allMessage: i18next.t('ADMIN.FTP_DEBUG.STATUS_FILTER_ALL_MESSAGE'),
                    },
                  ]}
                  filters={filters}
                  groups={groups}
                  readOnly={isLoading}
                  selectedGroups={selectedGroups}
                  selectedStores={selectedStores}
                  setAdvancedFilters={setAdvancedFilters}
                  setApplyFilters={setApplyFilters}
                  setFilters={setFilters}
                  setSelectedGroups={setSelectedGroups}
                  setSelectedStores={setSelectedStores}
                  stores={stores}
                  textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
                  disableAdvancedFilter
                />
                {isAnyFilterActive && (
                  <FilterActiveIcon
                    alt="icon-filter-active"
                    src={
                      isLoading
                        ? '/images/icon-dot-active-grey-inpulse.svg'
                        : '/images/icon-dot-active-green-inpulse.svg'
                    }
                  />
                )}
              </FilterButtonWrapper>
              <PeriodDatePicker
                disabled={isLoading}
                endDate={periodPickerState.endDate}
                focusedDateInput={periodPickerState.focusedDateInput}
                maxFutureDate={
                  !!periodPickerState.startDate && !!periodPickerState.endDate
                    ? YESTERDAY
                    : maxFutureDate
                }
                setFocusedDateInput={periodPickerState.setFocusedDateInput}
                startDate={periodPickerState.startDate}
                timezone={USER_TIMEZONE}
                onDatesChange={periodPickerState.handleSelectedDates}
              />
              <LegendButtonContainer
                borderColor={theme.colors.greys.light}
                onClick={() => openGenericModal(getStatusLegendModalParams())}
              >
                <LegendButtonIcon src={'/images/inpulse/info-black-small.svg'} />
              </LegendButtonContainer>
            </GenericGapContainer>
          }
          right={
            <Button
              actions={[
                {
                  label: i18next.t('ADMIN.FTP_DEBUG.EXPORT_ACTION'),
                  icon: '/images/inpulse/file-download-black-small.svg',
                  handleClick: handleExportList,
                },
              ]}
              color={'inpulse-outline'}
              iconDropdown={'/images/inpulse/carret-black.svg'}
              iconOnLeft={false}
              label={i18next.t('GENERAL.ACTIONS')}
            />
          }
        />
        <DataGridPro
          apiRef={apiRef}
          columns={columns}
          loading={isLoading}
          localeText={
            DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid.defaultProps
              .localeText
          }
          rows={filteredRows}
          {...COMMON_DATAGRID_PROPS}
        />
      </GenericContentContainer>
    </GenericPageContainer>
  );
};

const mapStateToProps = (state) => ({
  stores: state.baseReducer.activeStores,
  currency: state.baseReducer.currency,
  user: state.baseReducer.user,
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(FtpDebug);
